
import {
  invokeShopifyQuery,
  itemFilterWithActive,
  ongoingCardsQuery,
  CARD,
} from "@/utils/shopify";
import processDateAvailable from "~/mixin/process-date-available-mixin";
import ProductMapMixin from "~/mixin/product-map-mixin";
import DeliveryWeekMixin from "~/mixin/delivery-week-mixin";
import FormatMixin from "~/mixin/format-mixin";
import DomElementLoader from "~/mixin/dom-element-loader-mixin";
import { getDateSuffix } from "~/utils/locale";
import { butter } from "~/plugins/buttercms";
import calCardMessageGenerator from "~/components/cal-card-message-generator.vue";
import ImageCropper from "~/components/cal-img-cropper.vue";

export default {
  name: "cal-card-message",
  components: { calCardMessageGenerator, ImageCropper },
  mixins: [
    processDateAvailable,
    ProductMapMixin,
    DeliveryWeekMixin,
    FormatMixin,
    DomElementLoader,
  ],
  data() {
    return {
      cards: [] as any[],
      exclusiveCards: [] as any[],
      selectedCard: { ...this.$store.state.cartItem.card },
      selectedCardName: "",
      messageWriteWindow: false,
      msgTemplates: [
        {
          message: "",
          title: "",
        },
      ],
      msgWriteWindowHeader: "",
      selectedMsgTemplates: [] as any,
      cardMessages:
        { ...this.$store.state.cartItem.cardMessages } || (new Object() as any),
      cardSignature: this.$store.state.cartItem.cardSignature || "",
      msgMaxCharacters: 900,
      remainingMsgChars: [] as number[],
      msgsMaxCharSet: [],
      isAiMessageLoading: false,
      region: "",
      relationship: this.$store.state.cartItem.recipient.relationship || "",
      occasions: [],
      imageUploader: false,
      cardImageUploaded: false,
      showError: false,
    };
  },
  mounted() {
    if (this.$store.state.cartItem.card?.title) {
      this.selectedCardName = this.$store.state.cartItem.card?.title;
    }
  },
  computed: {
    subscriptionDeliveryDates() {
      return this.$store.state.cartItem.subscriptionDates;
    },
    isMobile() {
      return window.matchMedia("(max-width: 768px)").matches;
    },
  },
  props: {
    workflow: {
      type: String,
      required: false,
    },
    config: {
      type: Object,
      required: false,
    },
    numMessages: {
      type: Number,
      required: false,
      default: 1,
    },
  },
  async created() {
    this.region = sessionStorage.deliveryTown ? this.getRegion() : "";
    await this.loadCards();

    this.$nuxt.$emit("cardMessageDisabled", true);

    this.$nuxt.$on(
      "updateCardMessage",
      (cardMessage: string, index: number) => {
        this.cardMessages[this.getMessageKeyName(index)] = cardMessage;
        this.$store.dispatch("cartItem/CARD_MESSAGES_ADD", {
          ...this.cardMessages,
        });
        this.messageValidation();
      }
    );

    this.$nuxt.$on(
      "updateCardSignature",
      (signature: string, index: number) => {
        this.cardSignature = signature;
        this.$store.dispatch("cartItem/CARD_SIGNATURE_ADD", signature);
        this.messageValidation();
      }
    );

    this.$nuxt.$on("cityUpdated", async () => {
      await this.loadCards();
    });

    this.$nuxt.$on("deliveryDateUpdated", async () => {
      await this.loadCards();
    });

    this.$nuxt.$on("occasionSelected", async () => {
      await this.loadCards();
    });

    this.$nuxt.$on("messageImageUploaded", (status) => {
      this.cardImageUploaded = status;
      if (status) {
        this.$nuxt.$emit("cardMessageDisabled", false);
      }
    });

    this.$nuxt.$on("runInputFieldsValidation", () => {
      this.showError = true;
      this.messageValidation(true);
    });

    if (
      this.config?.isSubscription &&
      JSON.stringify(this.cardMessages) === "{}"
    ) {
      for (let i = 0; i < this.numMessages; i++) {
        this.cardMessages[this.getMessageKeyName(i)] = "";
      }
      this.cardSignature = "";
    } else if (
      this.config?.isSubscription &&
      JSON.stringify(this.cardMessages) !== "{}"
    ) {
      for (let i = 0; i < this.numMessages; i++) {
        if (!this.cardMessages[this.getMessageKeyName(i)]) {
          this.cardMessages[this.getMessageKeyName(i)] = "";
        }
        this.cardSignature = "";
      }
    } else if (JSON.stringify(this.cardMessages) === "{}") {
      this.cardMessages = { Message: "" };
      this.cardSignature = "";
    }

    if (this.config?.isSubscription) {
      let newCardMessages = {};
      let msgKeys = Object.keys(this.cardMessages);

      for (let i = 0; i < this.subscriptionDeliveryDates.length; i++) {
        newCardMessages[msgKeys[i]] = this.cardMessages[msgKeys[i]];
      }
      this.cardMessages = newCardMessages;
      this.cardSignature = "";
      this.$store.dispatch("cartItem/CARD_MESSAGES_ADD", {
        ...this.cardMessages,
      });
      this.$store.dispatch("cartItem/CARD_SIGNATURE_ADD", this.cardSignature);
    }

    if (this.selectedCard && Object.keys(this.selectedCard).length !== 0) {
      this.messageWriteWindow = true;
    }

    for (let i = 0; i < this.numMessages; i++) {
      this.occasions.push(sessionStorage.occasion);
    }
    this.messageValidation();

    if (
      Object.keys(this.selectedCard).length !== 0 &&
      this.selectedCard?.tags?.includes("photo-upload")
    ) {
      this.imageUploader = true;
    }
    this.$nuxt.$on("imageUploaderView", () => {
      const imageUploader = document.getElementById("image-uploader");
      if (imageUploader) {
        imageUploader.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
  },
  methods: {
    scrollToElement(id) {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    },
    scrollLeft() {
      const container = this.$refs.cardScrollContainer;
      container.scrollLeft -= 100;
    },
    scrollRight() {
      const container = this.$refs.cardScrollContainer;
      container.scrollLeft += 100;
    },
    updateRelationship(relationshipStr: string) {
      this.relationship = relationshipStr;
      this.messageValidation();
    },
    updateOccasion(occasionStr: string, index: number) {
      this.occasions[index] = occasionStr;
      this.messageValidation();
    },
    updateCardSignature(signatureStr: string) {
      this.cardSignature = signatureStr;
      this.messageValidation();
    },
    generateOccasionTag(occasion: string) {
      let tag = occasion.trim().replace(/ /g, "-");
      return `occasion:${tag.toLowerCase()}`;
    },

    cardSelect(card: any) {
      if (card?.tags?.includes("photo-upload")) {
        this.imageUploader = true;
      } else {
        this.imageUploader = false;
      }
      this.selectedCardName = card.title;
      this.messageWriteWindow = true;
      this.$store.dispatch("cartItem/CARD_ADD", { ...card });
      this.selectedCard = { ...this.$store.state.cartItem.card };
      this.waitForElementToScroll("selectedCard");
      this.messageValidation();
      this.$nuxt.$emit("cardUpdated");
    },

    changeCard() {
      this.imageUploader = false;
      this.messageWriteWindow = false;
    },
    getMessageKeyName(i: any) {
      return this.numMessages && this.config?.isSubscription
        ? `${i + 1}${this.getDateSuffix(i)} Message`
        : "Message";
    },
    getSignatureKeyName(i: any) {
      return this.numMessages && this.config?.isSubscription
        ? `${i + 1}${this.getDateSuffix(i)} Signature`
        : "Signature";
    },
    messageValidation(scrollToError = false) {
      if (!(this.selectedCardName && this.selectedCardName.length > 0)) {
        if (scrollToError) {
          this.scrollToElement("card-selection");
        }
        this.$nuxt.$emit("cardMessageDisabled", true);
        return;
      }

      if (this.relationship == null || this.relationship === "") {
        if (scrollToError) {
          this.scrollToElement("card_info");
        }
        this.$nuxt.$emit("cardMessageDisabled", true);
        return;
      }

      this.occasions.forEach((item) => {
        if (item === null || item === "") {
          if (scrollToError) {
            this.scrollToElement("card_info");
          }
          this.$nuxt.$emit("cardMessageDisabled", true);
          return;
        }
      });

      if (this.cardMessages) {
        let index = 0;
        for (const item of Object.values(this.cardMessages)) {
          if (item === null || item === "") {
            if (scrollToError) {
              this.scrollToElement(`card-message-${index}`);
            }
            this.$nuxt.$emit("cardMessageDisabled", true);
            return;
          }
          index = index + 1;
        }
      }

      if (this.cardSignature == null || this.cardSignature === "") {
        this.scrollToElement("card-signature-0");
        this.$nuxt.$emit("cardMessageDisabled", true);
        return;
      }

      if (this.imageUploader && !this.cardImageUploaded) {
        this.$nuxt.$emit("cardMessageDisabled", true);
        return;
      }

      this.$nuxt.$emit("cardMessageDisabled", false);
    },
    getDateSuffix(index: any) {
      return getDateSuffix(this.$i18n.locale, index + 1, true);
    },
    getPrice(price: any) {
      return this.getPriceWithLocale(price);
    },
    sortByRankDescending(cards) {
      return cards.sort((a, b) => {
        const rankA = a.tags?.find((tag) => tag.match(/rank-(\d+)/));
        const rankB = b.tags?.find((tag) => tag.match(/rank-(\d+)/));

        const numA = rankA
          ? parseInt(rankA.match(/rank-(\d+)/)[1], 10)
          : Infinity;
        const numB = rankB
          ? parseInt(rankB.match(/rank-(\d+)/)[1], 10)
          : Infinity;

        return numA - numB;
      });
    },
    async loadCards() {
      if (
        this.$store.state.cartItem.deliveryTown === undefined ||
        sessionStorage.deliveryTown === undefined ||
        sessionStorage.deliveryTown === "{}" ||
        sessionStorage.deliveryDate == "" ||
        sessionStorage.deliveryDate == undefined
      ) {
        return;
      }
      let occasion = sessionStorage["occasion"];

      const occasionsTag =
        occasion && occasion?.length > 0
          ? this.generateOccasionTag(occasion)
          : "card"; //card is default tag that every card has

      try {
        const region = this.getRegion().regionShortCode;
        const weekCode = this.selectWeekNumber(this.getDeliveryDate());
        let shopifyCards = {};
        if (this.workflow == "NON_FLORAL") {
          shopifyCards = await invokeShopifyQuery(
            itemFilterWithActive("TR", CARD)
          );
        } else if (this.workflow == "SUBSCRIPTION") {
          shopifyCards = await invokeShopifyQuery(
            ongoingCardsQuery(region, CARD)
          );
        } else {
          shopifyCards = await invokeShopifyQuery(
            itemFilterWithActive(region, CARD)
          );
        }

        const activeCards = this.mapShopifyProducts(shopifyCards.data).filter(
          (card: { tags: string | string[] }) => {
            const tags = card.tags;
            return tags.includes("card") && tags.includes("active");
          }
        );

        this.cards = activeCards.filter((card: { tags: string | string[] }) => {
          const tags = card.tags;
          return (
            !tags.includes("exclusive-card") &&
            (tags.includes(weekCode) || tags.includes("card-ongoing")) &&
            !tags.includes("not-" + weekCode) &&
            (tags.includes(occasionsTag) || tags.includes("occasion:all"))
          );
        });

        this.cards = this.sortByRankDescending(this.cards);

        this.exclusiveCards = activeCards.filter(
          (card: { tags: string | string[] }) => {
            const tags = card.tags;
            return (
              (tags.includes(weekCode) || tags.includes("card-ongoing")) &&
              tags.includes("exclusive-card") &&
              (tags.includes(occasionsTag) || tags.includes("occasion:all"))
            );
          }
        );
        this.cards = this.exclusiveCards.concat(this.cards);
      } catch (error) {
        throw new Error(`Oops something is not right!!! ${error}`);
      }

      // get card message templates from CMS
      try {
        const cardMessages = await butter(this.$config).content.retrieve(
          ["card_messages"],
          {
            locale: this.$i18n.locale,
            timeout: 10000,
          }
        );
        let templatesData = cardMessages?.data.data.card_messages[0];
        if (JSON.stringify(templatesData) !== "{}") {
          this.msgTemplates = templatesData.message_templates;
          this.msgWriteWindowHeader = templatesData.header;
          this.msgMaxCharacters = templatesData.msg_max_chars;
        }
      } catch (error) {
        console.error(error);
      }
    },
  },
};
